import { RefObject, useEffect } from "react";

interface UseAutoFitHtmlParams {
    containerRef: RefObject<HTMLElement | null>;
    contentRef: RefObject<HTMLElement | null>;
    html: string;
    minFontSize?: number;
    maxFontSize?: number;
}

export function useAutoFitHtml({
    containerRef,
    contentRef,
    html,
    minFontSize = 8,
    maxFontSize = 72,
}: UseAutoFitHtmlParams) {
    useEffect(() => {
        let timer: any = null

        const fitContent = () => {
            const container = containerRef.current;
            const content = contentRef.current;

            if (!container || !content) return;

            // Disconnect while mutating styles to prevent feedback loop
            observer.disconnect();

            let low = minFontSize;
            let high = maxFontSize;
            let best = minFontSize;

            while (low <= high) {
                const mid = Math.floor((low + high) / 2);

                content.style.fontSize = `${mid}px`;

                const fits =
                    content.scrollWidth <= container.clientWidth &&
                    content.scrollHeight <= container.clientHeight;

                if (fits) {
                    best = mid;
                    low = mid + 1;
                } else {
                    high = mid - 1;
                }
            }

            content.style.fontSize = `${best}px`;

            if (containerRef.current) {
                observer.observe(containerRef.current);
            }
        };

        const debouncedFit = () => {
            clearTimeout(timer);
            timer = setTimeout(fitContent, 150);
        };

        const initialize = async () => {
            if ("fonts" in document) {
                await document.fonts.ready;
            }

            fitContent();
        };

        const observer = new ResizeObserver(debouncedFit);

        initialize();

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            clearTimeout(timer);
            observer.disconnect();
        };
    }, [
        html,
        minFontSize,
        maxFontSize,
        containerRef,
        contentRef,
    ]);
}